import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";
import { getAccessToken } from "@/lib/authToken";
import { getAuthMe } from "@/lib/api";
import {
  ENTITLEMENT_SESSION_CACHE_KEY,
  clearSessionEntitlementCache,
  getStoredSubscribedPlan,
} from "@/lib/subscriptionStorage";

function subscribeAuth(onChange) {
  const handler = () => onChange();
  window.addEventListener("storage", handler);
  window.addEventListener("rareRoles:auth-changed", handler);
  return () => {
    window.removeEventListener("storage", handler);
    window.removeEventListener("rareRoles:auth-changed", handler);
  };
}

/** Snapshot is the raw token so swaps while still "logged in" invalidate state. */
function getTokenSnapshot() {
  return typeof window === "undefined" ? "" : getAccessToken() || "";
}

function readEntitlementCache(token) {
  if (!token || typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(ENTITLEMENT_SESSION_CACHE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw);
    if (o?.t !== token) return null;
    if (o.has_access === true) return true;
    if (o.has_access === false) return false;
    return null;
  } catch {
    return null;
  }
}

function writeEntitlementCache(token, hasAccess) {
  if (!token || typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(
      ENTITLEMENT_SESSION_CACHE_KEY,
      JSON.stringify({ t: token, has_access: hasAccess })
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function computeEntitlementState(token) {
  if (!token) {
    return { hasAccess: false, accessResolved: true };
  }
  const cached = readEntitlementCache(token);
  if (cached !== null) {
    return { hasAccess: cached, accessResolved: true };
  }
  if (getStoredSubscribedPlan()) {
    return { hasAccess: true, accessResolved: true };
  }
  return { hasAccess: false, accessResolved: false };
}

const EntitlementContext = createContext(null);

export function EntitlementProvider({ children }) {
  const token = useSyncExternalStore(subscribeAuth, getTokenSnapshot, () => "");
  const [accessResolved, setAccessResolved] = useState(
    () => computeEntitlementState(getTokenSnapshot()).accessResolved
  );
  const [hasAccess, setHasAccess] = useState(
    () => computeEntitlementState(getTokenSnapshot()).hasAccess
  );

  useLayoutEffect(() => {
  if (!token) {
    clearSessionEntitlementCache();
      setHasAccess(false);
      setAccessResolved(true);
      return;
    }
    const next = computeEntitlementState(token);
    setHasAccess(next.hasAccess);
    setAccessResolved(next.accessResolved);
  }, [token]);

  const refreshEntitlement = useCallback(async () => {
    const t = getAccessToken();
    if (!t) {
      clearSessionEntitlementCache();
      setAccessResolved(true);
      setHasAccess(false);
      return false;
    }
    const cached = readEntitlementCache(t);
    const stayWarm = cached !== null || Boolean(getStoredSubscribedPlan());
    if (!stayWarm) {
      setAccessResolved(false);
    }
    try {
      const data = await getAuthMe();
      if (getAccessToken() !== t) {
        return false;
      }
      const ok = Boolean(data?.subscription?.has_access);
      writeEntitlementCache(t, ok);
      setHasAccess(ok);
      return ok;
    } catch {
      if (getAccessToken() === t && !stayWarm) {
        setHasAccess(false);
      }
      return false;
    } finally {
      if (getAccessToken() === t) {
        setAccessResolved(true);
      }
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    refreshEntitlement();
  }, [token, refreshEntitlement]);

  useEffect(() => {
    const onSubscription = () => refreshEntitlement();
    window.addEventListener("rareroles:subscription-changed", onSubscription);
    return () => window.removeEventListener("rareroles:subscription-changed", onSubscription);
  }, [refreshEntitlement]);

  const entitlementLoading = Boolean(token) && !accessResolved;

  const value = useMemo(
    () => ({
      hasAccess,
      entitlementLoading,
      refreshEntitlement,
    }),
    [hasAccess, entitlementLoading, refreshEntitlement]
  );

  return (
    <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>
  );
}

export function useEntitlement() {
  const ctx = useContext(EntitlementContext);
  if (!ctx) {
    throw new Error("useEntitlement must be used within EntitlementProvider");
  }
  return ctx;
}
